import { useEffect, useLayoutEffect, useRef } from 'react'

import { onLiveResync, type LiveArea, type LiveChange } from '../services/signalr'

import { useSignalR } from './useSignalR'

/**
 * تحديثٌ حيّ لشاشة: تعيد التحميل حين يصل `CrmChanged` لإحدى `areas`.
 *
 *   useLiveRefresh(['media-leads'], () => load({ silent: true }))
 *
 * - دفعة أحداث متقاربة (توزيع ٣٠ اسماً = ٣٠ حدثاً) تُجمع بتحميلٍ واحد بعد 400ms.
 * - عودة التبويب للظهور بعد غياب تعيد التحميل أيضاً — ما فات أثناء الخفاء لا يضيع.
 * - وعودة الاتصال الحيّ بعد انقطاع (نشر، شبكة) تعيده كذلك — ما بُثّ أثناءه فات.
 * - يقرأ آخر `reload` عبر مرجع فلا يعاد الاشتراك مع كل إعادة رسم.
 */
export function useLiveRefresh(areas: LiveArea[], reload: () => void, debounceMs = 400) {
  const reloadRef = useRef(reload)
  useLayoutEffect(() => {
    reloadRef.current = reload
  })
  const areasRef = useRef(areas)
  useLayoutEffect(() => {
    areasRef.current = areas
  })
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const schedule = () => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      timer.current = null
      reloadRef.current()
    }, debounceMs)
  }

  useSignalR<LiveChange>('CrmChanged', (change) => {
    if (change && areasRef.current.includes(change.area)) schedule()
  })

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') schedule()
    }
    document.addEventListener('visibilitychange', onVisible)
    const offResync = onLiveResync(schedule)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      offResync()
      if (timer.current) clearTimeout(timer.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
